<template>
  <div
    class="ask-a-question-attachment-card"
    :data-testid="`ask-a-question-attachment-${index}`"
    :tabindex="cardTabIndex"
  >
    <div class="ask-a-question-attachment-card__preview">
      <button
        v-if="isImage"
        type="button"
        class="ask-a-question-attachment-card__image-btn"
        :aria-label="`Preview ${attachment.name}`"
        :disabled="disabled || !previewSrc"
        data-testid="ask-a-question-attachment-image-open"
        @click="emitImagePreview"
      >
        <img
          class="ask-a-question-attachment-card__image"
          :src="previewSrc"
          :alt="attachment.name"
        />
      </button>
      <p
        v-else-if="isPdf && showPdfFilenameFallback"
        class="ask-a-question-attachment-card__filename"
      >
        {{ truncatedName }}
      </p>
      <div
        v-else-if="isPdf"
        ref="pdfThumbRef"
        class="ask-a-question-attachment-card__pdf"
        data-testid="ask-a-question-attachment-pdf"
      >
        <span v-if="pdfState === 'loading'" class="ask-a-question-attachment-card__placeholder">
          …
        </span>
      </div>
      <p v-else class="ask-a-question-attachment-card__filename">
        {{ truncatedName }}
      </p>
      <span
        v-if="showBadge"
        class="ask-a-question-attachment-card__badge"
        :class="badgeModifierClass"
      >
        {{ badgeLabel }}
      </span>
    </div>
    <button
      v-if="!readonly"
      type="button"
      class="ask-a-question-attachment-card__remove"
      :aria-label="`Remove ${attachment.name}`"
      :disabled="disabled"
      data-testid="ask-a-question-attachment-remove"
      @click="emit('remove')"
    >
      ×
    </button>
  </div>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { getPdfDocument } from "@components/FilePreview/renderPdfToContainer";
import {
  badgeLabelFromExtension,
  extensionFromAttachment,
  isImageAttachment,
  isPdfAttachment,
  truncateAttachmentName,
} from "./resolveAttachmentPreview";

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["remove", "preview-image"]);

const pdfThumbRef = ref(null);
const pdfState = ref("idle");
const objectUrl = ref("");
let pdfRenderId = 0;
let pdfResizeObserver = null;
let loadedPdf = null;
let loadedPdfSource = "";

const extension = computed(() => extensionFromAttachment(props.attachment));
const isImage = computed(() => isImageAttachment(props.attachment));
const isPdf = computed(() => isPdfAttachment(props.attachment));

const cardTabIndex = computed(() => {
  if (props.readonly || props.disabled) {
    return undefined;
  }
  if (isImage.value) {
    return undefined;
  }
  return 0;
});
const hasPdfSource = computed(() =>
  Boolean(props.attachment?.file || props.attachment?.previewUrl)
);
const showPdfFilenameFallback = computed(
  () => isPdf.value && (!hasPdfSource.value || pdfState.value === "error")
);
const badgeLabel = computed(() =>
  badgeLabelFromExtension(props.attachment.extension || extension.value)
);
const truncatedName = computed(() => truncateAttachmentName(props.attachment.name));
const showBadge = computed(() => !isImage.value);
const badgeModifierClass = computed(() => {
  const label = badgeLabel.value;
  if (label === "PDF") {
    return "ask-a-question-attachment-card__badge--pdf";
  }
  if (label === "DOC" || label === "DOCX") {
    return "ask-a-question-attachment-card__badge--doc";
  }
  return "";
});

const previewSrc = computed(() => {
  if (props.attachment.previewUrl) {
    return props.attachment.previewUrl;
  }
  return objectUrl.value;
});

const emitImagePreview = () => {
  if (!previewSrc.value) {
    return;
  }
  emit("preview-image", {
    src: previewSrc.value,
    name: props.attachment.name,
  });
};

const pdfSourceUrl = computed(() => {
  if (props.attachment.previewUrl) {
    return props.attachment.previewUrl;
  }
  if (isPdfAttachment(props.attachment) && objectUrl.value) {
    return objectUrl.value;
  }
  return "";
});

function revokeObjectUrl() {
  if (objectUrl.value && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
    URL.revokeObjectURL(objectUrl.value);
  }
  objectUrl.value = "";
}

function syncBlobUrl() {
  revokeObjectUrl();
  const blob = props.attachment?.file;
  if (!blob || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    return;
  }
  objectUrl.value = URL.createObjectURL(blob);
}

function disconnectPdfObserver() {
  if (pdfResizeObserver) {
    pdfResizeObserver.disconnect();
    pdfResizeObserver = null;
  }
}

async function destroyLoadedPdf() {
  if (loadedPdf && typeof loadedPdf.destroy === "function") {
    try {
      await loadedPdf.destroy();
    } catch {
      // ignore worker teardown races
    }
  }
  loadedPdf = null;
  loadedPdfSource = "";
}

async function paintPdfPage(container, requestId) {
  if (!loadedPdf || !container || requestId !== pdfRenderId) {
    return;
  }
  const width = Math.floor(container.clientWidth);
  if (!width) {
    return;
  }

  const page = await loadedPdf.getPage(1);
  const unscaled = page.getViewport({ scale: 1 });
  const dpr =
    typeof window !== "undefined" && window.devicePixelRatio ? window.devicePixelRatio : 1;
  const scale = (width / unscaled.width) * dpr;
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(viewport.width));
  canvas.height = Math.max(1, Math.floor(viewport.height));
  canvas.style.display = "block";
  canvas.style.width = "100%";
  canvas.style.height = "auto";
  const context = canvas.getContext("2d");
  await page.render({ canvasContext: context, viewport }).promise;
  if (requestId !== pdfRenderId || !pdfThumbRef.value) {
    return;
  }
  container.innerHTML = "";
  container.appendChild(canvas);
  pdfState.value = "ready";
}

function observePdfContainer(container, requestId) {
  disconnectPdfObserver();
  if (!container || typeof ResizeObserver === "undefined") {
    return;
  }
  pdfResizeObserver = new ResizeObserver(() => {
    paintPdfPage(container, requestId).catch(() => {
      if (requestId === pdfRenderId) {
        pdfState.value = "error";
      }
    });
  });
  pdfResizeObserver.observe(container);
}

async function renderPdfThumb() {
  if (!isPdf.value || !hasPdfSource.value || !pdfThumbRef.value) {
    return;
  }

  const requestId = ++pdfRenderId;
  pdfState.value = "loading";
  pdfThumbRef.value.innerHTML = "";

  const url = pdfSourceUrl.value;
  if (!url) {
    pdfState.value = "error";
    return;
  }

  try {
    await nextTick();
    const container = pdfThumbRef.value;
    if (!container || requestId !== pdfRenderId) {
      return;
    }

    if (!loadedPdf || loadedPdfSource !== url) {
      await destroyLoadedPdf();
      loadedPdf = await getPdfDocument(url);
      loadedPdfSource = url;
    }

    await paintPdfPage(container, requestId);
    if (requestId !== pdfRenderId) {
      return;
    }

    if (pdfState.value !== "ready" && container.clientWidth === 0) {
      observePdfContainer(container, requestId);
      return;
    }

    observePdfContainer(container, requestId);
  } catch {
    if (requestId === pdfRenderId) {
      pdfState.value = "error";
    }
  }
}

watch(
  () => [props.attachment?.file, props.attachment?.previewUrl, isImage.value, isPdf.value],
  async () => {
    disconnectPdfObserver();
    await destroyLoadedPdf();
    syncBlobUrl();
    if (isPdf.value && hasPdfSource.value) {
      await nextTick();
      await renderPdfThumb();
    } else if (isPdf.value) {
      pdfState.value = "error";
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  pdfRenderId += 1;
  disconnectPdfObserver();
  destroyLoadedPdf();
  revokeObjectUrl();
});
</script>

<style scoped>
.ask-a-question-attachment-card {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  min-width: 0;
}

.ask-a-question-attachment-card__preview {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.06);
  box-sizing: border-box;
}

.ask-a-question-attachment-card__image-btn {
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
}

.ask-a-question-attachment-card__image-btn:disabled {
  cursor: not-allowed;
}

.ask-a-question-attachment-card__image-btn:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: -2px;
}

.ask-a-question-attachment-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.ask-a-question-attachment-card__pdf {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: 0;
  box-sizing: border-box;
}

.ask-a-question-attachment-card__filename {
  margin: 0;
  padding: 10px 8px 24px;
  font-family: Inter, sans-serif;
  font-size: 11px;
  line-height: 14px;
  font-weight: 500;
  color: #374151;
  word-break: break-word;
}

.ask-a-question-attachment-card__placeholder {
  margin: auto;
  color: #9ca3af;
  font-size: 18px;
}

.ask-a-question-attachment-card__badge {
  position: absolute;
  left: 6px;
  bottom: 6px;
  padding: 2px 6px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #e5e7eb;
  font-family: Inter, sans-serif;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #374151;
}

.ask-a-question-attachment-card__badge--pdf {
  color: #b91c1c;
}

.ask-a-question-attachment-card__badge--doc {
  color: #1d4ed8;
}

.ask-a-question-attachment-card__remove {
  position: absolute;
  top: -6px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #ffffff;
  color: #6b7280;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.08);
}

.ask-a-question-attachment-card__remove:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.ask-a-question-attachment-card__remove:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

.ask-a-question-attachment-card:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
  border-radius: 12px;
}
</style>
